import { addToast } from '@features/components/Notification/notificationSlice.ts';
import i18n from '@providers/i18n';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import localStore from '@shared/utils/localStore.ts';
import { Mutex } from 'async-mutex';

import { logout, updateTokens } from './reducers/authSlice.ts';
import type { TokensDto } from './services/authApi.ts';

interface ErrorResponse {
  code: string;
  message: string;
  module: string;
}

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_PATH,
  prepareHeaders: headers => {
    // this method should retrieve the token without a hook
    const { accessToken } = localStore.getTokens();

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
  credentials: 'include', // Important for CORS with credentials
});

// Separate base query for refresh token requests (no Authorization header)
const refreshTokenQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_PATH,
  prepareHeaders: headers => {
    // Don't add Authorization header for refresh requests
    // The refresh token will be passed as a custom header
    return headers;
  },
  credentials: 'include', // Important for CORS with credentials
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  const tokens =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    api.getState().auth.tokens ?? localStore.getTokens();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const userId = api.getState().auth.userId ?? localStore.getUserId();
  let result = await baseQuery(args, api, extraOptions);

  const status = result.error?.status;

  if (status === 403) {
    window.location.replace('/permissionDenied');
    return result;
  }

  if (result.error && Number(result.error.status) === 400) {
    const errorResponse = result.error.data as ErrorResponse;
    const message = i18n.t(`${errorResponse.module}.errors.${errorResponse.code}`, {
      defaultValue: errorResponse.message,
    });
    api.dispatch(addToast({ message, type: 'error' }));

    return result;
  }

  if (result.error && Number(result.error.status) > 401) {
    const message =
      'data' in result.error
        ? `${(result.error.data as { message: string }).message}, please try again later.`
        : 'Unable to reach the server, please try again later.';
    api.dispatch(addToast({ message, type: 'error' }));

    return result;
  }

  if (result.error && Number(result.error.status) === 401) {
    // Only attempt token refresh if we have both tokens and userId
    if (tokens?.accessToken && tokens?.refreshToken && userId) {
      // checking whether the mutex is locked
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
          // console.log('🔄 Attempting to refresh token...');
          const refreshResult = await refreshTokenQuery(
            {
              url: `/auth/refresh/${userId}`,
              headers: {
                'Refresh-Token': tokens.refreshToken,
              },
              method: 'GET',
            },
            api,
            extraOptions,
          );

          if (refreshResult.data) {
            // console.log('✅ Token refresh successful, retrying original request');
            api.dispatch(updateTokens(refreshResult.data as TokensDto));
            // retry the initial query with new token
            result = await baseQuery(args, api, extraOptions);
          } else {
            // console.log('❌ Token refresh failed, logging out user');
            api.dispatch(logout());
            api.dispatch(addToast({ message: i18n.t('tips.session-expired'), type: 'warning' }));
          }
        } catch (_error) {
          // console.error('❌ Token refresh error:', error);
          api.dispatch(logout());
          api.dispatch(addToast({ message: i18n.t('tips.session-expired'), type: 'warning' }));
        } finally {
          // release must be called once the mutex should be released again.
          release();
        }
      } else {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();
        // retry the request after the mutex is released (another request may have refreshed the token)
        result = await baseQuery(args, api, extraOptions);
      }
    } else {
      // No valid tokens, logout immediately
      // console.log('⚠️ No valid tokens found, logging out');
      api.dispatch(logout());
    }
  }

  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const entrySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
